import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  IconButton,
  Paper,
  alpha,
} from "@mui/material";

import {
  CloudUploadOutlined,
  DeleteOutline,
  InsertDriveFileOutlined,
  UploadFileOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";

// ----------------------------------------------------------------------

const API_URL =
  "https://middlewarebackprd.azurewebsites.net/api/v1/CargarCartera";

// ----------------------------------------------------------------------

function CargaTesoreria() {
  const [isLoading, setLoading] = useState(false);
  const [archivos, setArchivos] = useState([]);

  // --------------------------------------------------------------------
  // FORMATO TAMAÑO
  // --------------------------------------------------------------------

  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return "0 KB";

    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  }, []);

  // --------------------------------------------------------------------
  // SELECCIONAR ARCHIVOS
  // --------------------------------------------------------------------

  const subirArchivos = useCallback((event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;

    setArchivos((prev) => {
      const existingNames = new Set(
        prev.map((file) => `${file.name}-${file.size}`),
      );

      const nuevos = files.filter(
        (file) => !existingNames.has(`${file.name}-${file.size}`),
      );

      return [...prev, ...nuevos];
    });

    // Permite seleccionar nuevamente el mismo archivo
    event.target.value = "";
  }, []);

  // --------------------------------------------------------------------
  // ELIMINAR ARCHIVO
  // --------------------------------------------------------------------

  const eliminarArchivo = useCallback((index) => {
    setArchivos((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // --------------------------------------------------------------------
  // LIMPIAR ARCHIVOS
  // --------------------------------------------------------------------

  const limpiarArchivos = useCallback(() => {
    setArchivos([]);
  }, []);

  // --------------------------------------------------------------------
  // INFORMACIÓN DE CARGA
  // --------------------------------------------------------------------

  const totalSize = useMemo(
    () => archivos.reduce((total, file) => total + file.size, 0),
    [archivos],
  );

  // --------------------------------------------------------------------
  // INSERTAR ARCHIVOS
  // --------------------------------------------------------------------

  const insertarArchivos = useCallback(async () => {
    if (!archivos.length) {
      Swal.fire({
        icon: "warning",
        title: "Selecciona archivos",
        text: "Debes seleccionar al menos un archivo para continuar.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#0085CA",
      });

      return;
    }

    const username = "admin.icontec@icontec.org";
    const password = "quAngEraMuSTerGerEDE";

    const credentials = btoa(`${username}:${password}`);

    const authHeader = `Basic ${credentials}`;

    const formData = new FormData();

    archivos.forEach((archivo) => {
      formData.append("files", archivo);
    });

    setLoading(true);

    try {
      const response = await axios.post(API_URL, formData, {
        headers: {
          Authorization: authHeader,
          "Content-Type": "multipart/form-data",
        },

        // Timeout para evitar solicitudes colgadas indefinidamente
        timeout: 300000,

        // Progreso de subida
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );

          console.log(`Carga: ${percentCompleted}%`);
        },
      });

      console.log(response.data);

      await Swal.fire({
        icon: "success",
        title: "Carga completada",
        text: response.data || "Los archivos fueron cargados correctamente.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#0085CA",
      });

      setArchivos([]);

      window.location.reload();
    } catch (error) {
      console.error(error);

      let mensaje =
        "Hubo un problema al subir los archivos. Por favor comuníquese con el administrador.";

      if (error.code === "ECONNABORTED") {
        mensaje =
          "La carga tardó demasiado tiempo. Verifique su conexión e inténtelo nuevamente.";
      }

      if (error.response?.data) {
        mensaje =
          typeof error.response.data === "string"
            ? error.response.data
            : "El servidor rechazó la carga de los archivos.";
      }

      Swal.fire({
        icon: "error",
        title: "Error al cargar",
        text: mensaje,
        confirmButtonText: "Cerrar",
        confirmButtonColor: "#d32f2f",
      });
    } finally {
      setLoading(false);
    }
  }, [archivos]);

  // --------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 80px)",
        backgroundColor: "#F5F8FA",
        py: {
          xs: 3,
          md: 5,
        },
        px: {
          xs: 2,
          md: 4,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1000,
          mx: "auto",
        }}
      >
        {/* ========================================================== */}
        {/* ENCABEZADO */}
        {/* ========================================================== */}

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#1C2733",
              mb: 0.8,
            }}
          >
            Carga de documentos
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              maxWidth: 650,
            }}
          >
            Selecciona los documentos que deseas cargar al sistema. Puedes
            seleccionar varios archivos simultáneamente.
          </Typography>
        </Box>

        {/* ========================================================== */}
        {/* CARD PRINCIPAL */}
        {/* ========================================================== */}

        <Card
          elevation={0}
          sx={{
            borderRadius: "18px",
            border: "1px solid",
            borderColor: alpha("#0085CA", 0.1),
            boxShadow: "0 10px 35px rgba(31, 45, 61, 0.07)",
            overflow: "hidden",
          }}
        >
          <CardHeader
            avatar={
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha("#0085CA", 0.1),
                  color: "#0085CA",
                }}
              >
                <UploadFileOutlined />
              </Box>
            }
            title={
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#263238",
                }}
              >
                Adjuntar documentos
              </Typography>
            }
            subheader="Selecciona los archivos que deseas procesar"
            sx={{
              px: {
                xs: 2,
                md: 3,
              },
              pt: 3,
            }}
          />

          <Divider />

          <CardContent
            sx={{
              p: {
                xs: 2,
                md: 3,
              },
            }}
          >
            {/* ====================================================== */}
            {/* ZONA DE CARGA */}
            {/* ====================================================== */}

            <Paper
              component="label"
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",

                minHeight: 210,

                p: 3,

                borderRadius: "16px",

                border: `2px dashed ${alpha("#0085CA", 0.28)}`,

                backgroundColor: alpha("#0085CA", 0.025),

                cursor: isLoading ? "not-allowed" : "pointer",

                transition: "all 0.25s ease",

                "&:hover": {
                  backgroundColor: alpha("#0085CA", 0.05),

                  borderColor: "#0085CA",
                },
              }}
            >
              <input
                type="file"
                name="files"
                multiple
                hidden
                disabled={isLoading}
                onChange={subirArchivos}
              />

              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "18px",

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  background:
                    "linear-gradient(135deg, #006FA8 0%, #0085CA 100%)",

                  color: "#fff",

                  mb: 2,

                  boxShadow: "0 8px 20px rgba(0, 133, 202, 0.22)",
                }}
              >
                <CloudUploadOutlined
                  sx={{
                    fontSize: 32,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#263238",
                  mb: 0.5,
                }}
              >
                Selecciona tus documentos
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                Haz clic aquí para seleccionar uno o varios archivos
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  color: "text.disabled",
                }}
              >
                Los archivos serán procesados por el sistema
              </Typography>
            </Paper>

            {/* ====================================================== */}
            {/* INFORMACIÓN DE ARCHIVOS */}
            {/* ====================================================== */}

            {archivos.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      Archivos seleccionados
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {archivos.length} archivo
                      {archivos.length !== 1 ? "s" : ""} •{" "}
                      {formatFileSize(totalSize)}
                    </Typography>
                  </Box>

                  {!isLoading && (
                    <Button
                      size="small"
                      color="error"
                      onClick={limpiarArchivos}
                      startIcon={<DeleteOutline />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Stack>

                <List
                  disablePadding
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {archivos.map((archivo, index) => (
                    <ListItem
                      key={`${archivo.name}-${index}`}
                      disableGutters
                      sx={{
                        px: 1.5,
                        py: 1,

                        borderRadius: "10px",

                        border: "1px solid rgba(0,0,0,0.06)",

                        backgroundColor: "#FAFCFD",
                      }}
                      secondaryAction={
                        !isLoading && (
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => eliminarArchivo(index)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 42,
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "9px",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            backgroundColor: alpha("#0085CA", 0.09),

                            color: "#0085CA",
                          }}
                        >
                          <InsertDriveFileOutlined fontSize="small" />
                        </Box>
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 600,

                              maxWidth: {
                                xs: 190,
                                sm: 500,
                              },

                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {archivo.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(archivo.size)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* ====================================================== */}
            {/* LOADING */}
            {/* ====================================================== */}

            {isLoading && (
              <Box sx={{ mt: 3 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#0085CA",
                    }}
                  >
                    Cargando documentos...
                  </Typography>
                </Stack>

                <LinearProgress
                  sx={{
                    height: 6,
                    borderRadius: 10,

                    backgroundColor: alpha("#0085CA", 0.1),

                    "& .MuiLinearProgress-bar": {
                      borderRadius: 10,
                      background: "linear-gradient(90deg, #006FA8, #00A6D6)",
                    },
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1,
                    color: "text.secondary",
                  }}
                >
                  No cierres esta ventana mientras se procesan los documentos.
                </Typography>
              </Box>
            )}

            {/* ====================================================== */}
            {/* BOTONES */}
            {/* ====================================================== */}

            <Stack
              direction={{
                xs: "column-reverse",
                sm: "row",
              }}
              justifyContent="flex-end"
              spacing={1.5}
              sx={{ mt: 3 }}
            >
              {!isLoading && archivos.length > 0 && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={limpiarArchivos}
                  aceppt={".csv"}
                  sx={{
                    minHeight: 46,
                    px: 3,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Cancelar
                </Button>
              )}

              <Button
                variant="contained"
                disabled={isLoading || archivos.length === 0}
                startIcon={isLoading ? null : <CheckCircleOutline />}
                onClick={insertarArchivos}
                sx={{
                  minHeight: 46,
                  px: 3,

                  borderRadius: "10px",

                  textTransform: "none",

                  fontWeight: 700,

                  background:
                    "linear-gradient(90deg, #006FA8 0%, #0085CA 100%)",

                  boxShadow: "0 7px 18px rgba(0, 133, 202, 0.22)",

                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #005B8F 0%, #0074B3 100%)",

                    transform: "translateY(-1px)",
                  },

                  "&.Mui-disabled": {
                    backgroundColor: "#E2E8F0",
                  },

                  transition: "all 0.2s ease",
                }}
              >
                {isLoading
                  ? "Cargando..."
                  : `Guardar ${
                      archivos.length > 0 ? `(${archivos.length})` : ""
                    }`}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default CargaTesoreria;
